import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, ExternalLink, IndianRupee } from 'lucide-react';
import { getUserFacingBookingId } from '../../utils/userFacingBookingId';
import {
  makeOptimisticMessage,
  maxMessageCursor,
  mergeThreadMessages,
  useSupportTabTitle,
  useSupportThreadRealtime,
  useThreadScroll,
} from '../../utils/supportThreadRealtime';
import {
  SUPPORT_API,
  getCategoryLabel,
} from '../../components/support/supportConstants';
import { SupportStatusBadge } from '../../components/support/SupportStatusBadge';
import { CollapsibleBookingPanel } from '../../components/support/CollapsibleBookingPanel';
import { PendingPaymentBanner, PaymentsPanel } from '../../components/support/PaymentsPanel';
import { SupportMessageThread } from '../../components/support/SupportMessageThread';
import { SupportReplyComposer } from '../../components/support/SupportReplyComposer';

const BASE_PATH = '/agent/support-tickets';

const AgentSupportTicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyErr, setReplyErr] = useState('');
  const [attachUploading, setAttachUploading] = useState(false);
  const [attachErr, setAttachErr] = useState('');
  const sinceRef = useRef('');
  const [inboundPing, setInboundPing] = useState(0);
  const { threadRef, onThreadScroll, scrollToBottom, newCount } = useThreadScroll(data?.messages);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const r = await axios.get(`${SUPPORT_API}/support/tickets/${id}`, { withCredentials: true });
      setData(r.data);
      sinceRef.current = maxMessageCursor(r.data?.messages || []);
    } catch (e) {
      setData(null);
      setLoadError(e.response?.data?.detail || 'Could not load ticket');
    }
    setLoading(false);
  }, [id]);

  const handleThreadPoll = useCallback((payload) => {
    const incoming = payload?.messages || [];
    if (incoming.some((m) => m.sender_type === 'admin')) {
      setInboundPing((p) => p + 1);
    }
    setData((prev) => {
      if (!prev) return prev;
      const merged = mergeThreadMessages(prev.messages, incoming);
      sinceRef.current = maxMessageCursor(merged);
      const ticket = payload?.ticket ? { ...prev.ticket, ...payload.ticket } : prev.ticket;
      return { ...prev, messages: merged, ticket };
    });
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setInboundPing(0); }, [id]);

  useSupportThreadRealtime({
    ticketId: id,
    streamUrl: id ? `${SUPPORT_API}/support/tickets/${id}/stream` : '',
    pollUrl: id ? `${SUPPORT_API}/support/tickets/${id}/messages` : '',
    enabled: Boolean(!loading && data?.ticket),
    getSince: () => sinceRef.current,
    onPollResult: handleThreadPoll,
  });

  useSupportTabTitle({
    enabled: Boolean(data?.ticket),
    ticketRef: data?.ticket?.reference,
    ticketStatus: data?.ticket?.status,
    inboundPing,
  });

  const sendReply = async () => {
    const text = reply.trim();
    if (!text) return;
    const optimistic = makeOptimisticMessage({
      ticketId: id,
      senderType: 'agent',
      senderName: data?.ticket?.created_by_name || 'You',
      senderRole: 'agent',
      message: text,
    });
    setReply('');
    setReplyErr('');
    setData((prev) => (prev ? { ...prev, messages: [...(prev.messages || []), optimistic] } : prev));
    scrollToBottom();
    setSending(true);
    try {
      const r = await axios.post(
        `${SUPPORT_API}/support/tickets/${id}/reply`,
        { message: text },
        { withCredentials: true },
      );
      setData((prev) => {
        if (!prev) return prev;
        const without = (prev.messages || []).filter((m) => m.id !== optimistic.id);
        const merged = mergeThreadMessages(without, [r.data.message]);
        sinceRef.current = maxMessageCursor(merged);
        return { ...prev, messages: merged };
      });
    } catch (e) {
      setData((prev) => (
        prev ? { ...prev, messages: (prev.messages || []).filter((m) => m.id !== optimistic.id) } : prev
      ));
      setReply(text);
      setReplyErr(e.response?.data?.detail || 'Reply failed');
    }
    setSending(false);
  };

  const onAttachFiles = async (e) => {
    const fl = Array.from(e.target.files || []);
    e.target.value = '';
    if (!fl.length) return;
    setAttachUploading(true);
    setAttachErr('');
    try {
      const form = new FormData();
      fl.slice(0, 5).forEach((f) => form.append('files', f));
      const r = await axios.post(`${SUPPORT_API}/support/tickets/${id}/attach`, form, { withCredentials: true });
      setData((prev) => {
        if (!prev) return prev;
        const merged = mergeThreadMessages(prev.messages, [r.data.message]);
        sinceRef.current = maxMessageCursor(merged);
        return { ...prev, messages: merged };
      });
      scrollToBottom();
    } catch (err) {
      setAttachErr(err.response?.data?.detail || 'Upload failed');
    }
    setAttachUploading(false);
  };

  const pendingPayment = useMemo(() => {
    const list = data?.payments || [];
    return list.find((p) => p.type === 'ADDITIONAL_COLLECTION' && p.status === 'pending') || null;
  }, [data]);

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" />
      </div>
    );
  }
  if (loadError) {
    return <div className="p-12 text-center text-red-600 text-sm">{loadError}</div>;
  }
  if (!data?.ticket) {
    return <div className="p-12 text-center text-gray-500">Ticket not found.</div>;
  }

  const t = data.ticket;
  const bookingSearch = getUserFacingBookingId({ booking_id: t.booking_id });

  return (
    <div className="max-w-6xl mx-auto" data-testid="agent-support-detail">
      <button
        type="button"
        onClick={() => navigate(BASE_PATH)}
        className="text-sm text-gray-600 hover:text-[#1B375B] flex items-center gap-1 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back to My Support
      </button>

      <div className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur border-b border-gray-200 -mx-4 px-4 py-3 mb-4 lg:mx-0 lg:rounded-xl lg:border">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-mono text-xs text-teal-700">{t.reference}</div>
            <h1 className="font-bold text-lg text-[#1B375B]">{t.subject || getCategoryLabel(t.category)}</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {getCategoryLabel(t.category)} · Booking {bookingSearch}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SupportStatusBadge status={t.status} variant="agent" className="!text-xs" />
            <Link
              to={`/agent/bookings?search=${encodeURIComponent(bookingSearch)}`}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#1B375B] text-[#1B375B] font-semibold hover:bg-[#1B375B]/5"
              data-testid="agent-view-booking-link"
            >
              View booking <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>
        {t.status === 'need_reply' && (
          <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="agent-reply-needed-banner">
            ACF support is waiting for your reply on this ticket.
          </p>
        )}
      </div>

      {pendingPayment && (
        <div className="mb-4 sticky top-[120px] z-10">
          <PendingPaymentBanner ticketRef={id} payment={pendingPayment} onDone={load} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col min-h-[480px]">
            <div className="p-4 border-b border-gray-200 font-semibold text-gray-900">Conversation</div>
            <div className="flex-1 min-h-0">
              <SupportMessageThread
                messages={data.messages}
                ticketRef={t.id}
                ticket={t}
                theme="agent"
                threadRef={threadRef}
                onThreadScroll={onThreadScroll}
                newCount={newCount}
                onScrollToBottom={scrollToBottom}
                maxHeightClass="max-h-[min(70vh,640px)]"
              />
            </div>
            {t.status !== 'closed' && (
              <SupportReplyComposer
                reply={reply}
                onReplyChange={setReply}
                onSend={sendReply}
                onAttachFiles={onAttachFiles}
                sending={sending}
                attachUploading={attachUploading}
                attachErr={attachErr}
                replyErr={replyErr}
                disabled={false}
                theme="agent"
                placeholder="Reply to ACF support…"
              />
            )}
          </div>
        </div>

        <div className="lg:col-span-1 space-y-3">
          <CollapsibleBookingPanel
            booking={data.booking}
            snap={t.booking_snapshot}
            bookingId={t.booking_id}
            defaultOpen={false}
          />

          <PaymentsPanel payments={data.payments || []} />

          <div className="bg-white border border-gray-200 rounded-xl p-4 text-xs text-gray-600" data-testid="agent-payment-help">
            <p className="font-semibold text-gray-800 flex items-center gap-1 mb-2">
              <IndianRupee className="w-3.5 h-3.5" /> Payments on this ticket
            </p>
            <ul className="space-y-1.5 list-disc list-inside">
              <li><strong>COLLECT</strong> — extra amount requested by support (pay via wallet, credit, or gateway).</li>
              <li><strong>REFUND</strong> — money returned after resolution.</li>
              <li>Pending items must be paid before support can proceed.</li>
            </ul>
          </div>

          {t.status === 'closed' && t.refund_amount && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm">
              <strong className="text-emerald-800">Resolved</strong>
              <p className="text-xs text-gray-700 mt-1">
                Refund ₹{t.refund_amount.toLocaleString()}
                {t.refund_reference && <> · Ref {t.refund_reference}</>}
              </p>
            </div>
          )}

          {t.first_admin_reply_at && (
            <p className="text-[11px] text-gray-500 px-1">
              First support reply: {new Date(t.first_admin_reply_at).toLocaleString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentSupportTicketDetail;
